// Évolution pluriannuelle de la masse salariale (People → Planification du
// personnel → Evolution pluriannuelle). Agrège, par année :
//   - masse brute, charges salarié/employeur, salaire net, coût employeur —
//     via le plan CALCULÉ PAR LE SERVEUR (moteur migré du JavaScript le
//     2026-08-21) → chiffres IDENTIQUES à l'onglet Masse salariale planifiée ;
//   - EPT moyen et effectif de fin d'année (contrats + mouvements planifiés,
//     calcul local léger — pas un moteur financier).
import { getSocialChargesPlanPeople } from '../../api/socialChargesPlan';
import { parseLocalDate, contractActiveInMonth } from './salaryMass';

const sum = (arr) => arr.reduce((a, b) => a + b, 0);

function monthStartOf(s) {
  const d = parseLocalDate(s);
  return d ? new Date(d.getFullYear(), d.getMonth(), 1) : null;
}

// Un contrat par personne et par mois : on privilégie un contrat ouvert (sans
// end_date), sinon le plus récemment démarré. Identique à HrPlanningPage.
function pickContractForMonth(contracts, year, monthIdx) {
  const active = contracts.filter((c) => contractActiveInMonth(c, year, monthIdx));
  if (active.length === 0) return null;
  const open = active.filter((c) => c.end_date == null);
  const pool = open.length > 0 ? open : active;
  return pool.reduce((best, c) => (!best || (c.start_date || '') > (best.start_date || '') ? c : best), null);
}

// EPT et effectif mensuels d'une année, mouvements planifiés inclus (non bornés
// par la clôture, comme grossForMonth / l'onglet Masse salariale planifiée).
//   rate_change (personne) → taux absolu · departure (personne) → 0
//   hire (synthétique) → +fte · departure synthétique → −fte
export function fteHcMonthly(contracts = [], movements = [], year) {
  const byPerson = new Map();
  for (const c of contracts) {
    if (!c.person_id) continue;
    if (!byPerson.has(c.person_id)) byPerson.set(c.person_id, []);
    byPerson.get(c.person_id).push(c);
  }
  const persMovs = new Map();
  const synthMovs = [];
  for (const mv of movements) {
    if (mv.movement_type === 'bonus') continue;
    if (mv.person_id) {
      if (!persMovs.has(mv.person_id)) persMovs.set(mv.person_id, []);
      persMovs.get(mv.person_id).push(mv);
    } else {
      synthMovs.push(mv);
    }
  }

  const fte = [];
  const hc = [];
  for (let m = 0; m < 12; m += 1) {
    const cell = new Date(year, m, 1);
    let f = 0;
    let h = 0;
    for (const [pid, pcs] of byPerson) {
      const c = pickContractForMonth(pcs, year, m);
      if (!c) continue;
      let rate = Number(c.activity_rate || 0);
      for (const mv of persMovs.get(pid) || []) {
        const sd = monthStartOf(mv.start_month);
        if (!sd) continue;
        const ed = mv.end_month ? monthStartOf(mv.end_month) : null;
        if (cell >= sd && (!ed || cell <= ed)) {
          if (mv.movement_type === 'rate_change') rate = Number(mv.fte || 0);
          else if (mv.movement_type === 'departure') rate = 0;
        }
      }
      if (rate > 0) { f += rate; h += 1; }
    }
    for (const mv of synthMovs) {
      const sd = monthStartOf(mv.start_month);
      if (!sd) continue;
      const ed = mv.end_month ? monthStartOf(mv.end_month) : null;
      if (cell >= sd && (!ed || cell <= ed)) {
        const r = Number(mv.fte != null ? mv.fte : 1) || 0;
        if (mv.movement_type === 'departure') { f -= r; h -= 1; }
        else { f += r; h += 1; }
      }
    }
    fte.push(f);
    hc.push(h);
  }
  return { fte, hc };
}

// Agrégat annuel sur la plage `years`, chargé DEPUIS LE SERVEUR (un plan par
// année). `estimatedFrom` (année) : à partir de cette année, les taux sont
// repris de `ratesYear` (étiquetés « estimé » à l'écran). EPT moyen = moyenne
// des 12 mois ; effectif = décembre. Retourne une Promise de lignes annuelles.
export function fetchMultiYearPayroll({ tenantId, contracts = [], movements = [], years = [], estimatedFrom = null, ratesYear = null }) {
  return Promise.all(years.map((y) => {
    const estimated = estimatedFrom != null && y >= estimatedFrom;
    return getSocialChargesPlanPeople(tenantId, y, null, estimated ? ratesYear : null)
      .then((r) => (r.error ? null : r.data))
      .catch(() => null)
      .then((plan) => {
        const s12 = (a) => sum((a || []).slice(0, 12));
        const gross = s12(plan?.gross);
        const net = s12(plan?.net);
        const cout = s12(plan?.cout);
        const chargesEmpl = gross - net;       // part salarié
        const chargesEmpr = cout - gross;      // part employeur
        const { fte, hc } = fteHcMonthly(contracts, movements, y);
        const fteAvg = sum(fte) / 12;
        const hcEnd = hc[11];
        const costPerFte = fteAvg > 0 ? cout / fteAvg : 0;
        return {
          year: y,
          gross,
          net,
          chargesEmpl,
          chargesEmpr,
          cout,
          fteAvg,
          hcEnd,
          costPerFte,
          hasRates: Boolean(plan?.hasData),
          estimated,
        };
      });
  }));
}
